import { FormModel } from '../models/questionModel';
import { generateFormSummaryReport } from '../pages/ReportPage/pdf/formSummaryReport';
import { generatePrincipalRisk } from '../pages/ReportPage/pdf/principalRiskReport';
import { generateRiskControlReport } from '../pages/ReportPage/pdf/riskControlReport';
import { generateRiskManagementReport } from '../pages/ReportPage/pdf/riskManagementReport';
import { generateRiskManagementSummaryReport } from '../pages/ReportPage/pdf/riskManagementSummaryReport';
import useQuestionStore, { FormType } from '../store/QuestionStore';
import { useReportStore } from '../store/ReportStore';
import useRiskTransferStore from '../store/RiskTransferStore';

interface FormDetailsItem {
  title: string;
  type: FormType;
}
const formDetails: FormDetailsItem[] = [
  {
    title: 'LR',
    type: 'liquidation',
  },
  {
    title: 'FR',
    type: 'funding',
  },
  {
    title: 'CR',
    type: 'credit',
  },
  {
    title: 'CO',
    type: 'commodity',
  },
  {
    title: 'CU',
    type: 'currency',
  },
  {
    title: 'IR',
    type: 'interest',
  },
  {
    title: 'OR',
    type: 'operational',
  },
  {
    title: 'DR',
    type: 'disaster',
  },
  {
    title: 'CP',
    type: 'compliance',
  },
  {
    title: 'LG',
    type: 'legal',
  },
];

export type ReportType =
  | 'principalReport'
  | 'riskControlReport'
  | 'riskManagementReport'
  | 'riskManagementSummaryReport'
  | 'summaryReport';

const useReportGenerator = (type: ReportType) => {
  const {
    setPrincipalReportPDF,
    setRiskControlReportPDF,
    setRiskManagementReportPDF,
    setSummaryReportPDF,
    setIsGeneratingReport,
  } = useReportStore();
  const { setRiskManagementSummaryReportPDF } = useRiskTransferStore();
  const { getQuestionByForm } = useQuestionStore();

  const generateReportHook = async (data?: FormModel[] | string) => {
    setIsGeneratingReport(true);
    setPrincipalReportPDF(null);

    switch (type) {
      case 'principalReport': {
        const boxes = formDetails.map((p) => {
          const question = getQuestionByForm(p.type);
          question.getTotalMarks();

          if (question.isSignificant) {
            return {
              title: p.title,
              probabilityMarks: question.getProbabilityMarks() || 0,
              impactMarks: question.getImpactMarks() || 0,
            };
          } else {
            return null;
          }
        });

        const pdf = await generatePrincipalRisk(boxes);
        setPrincipalReportPDF(pdf);
        break;
      }
      case 'riskControlReport': {
        if (data && Array.isArray(data)) {
          const pdf = await generateRiskControlReport(data);
          setRiskControlReportPDF(pdf);
        } else {
          console.warn(
            'data is not provided or data is not FormModel[] when generating Risk Control Report'
          );
        }
        break;
      }
      case 'riskManagementReport': {
        if (data && typeof data == 'string') {
          const pdf = await generateRiskManagementReport(data);
          setRiskManagementReportPDF(pdf);
        } else {
          console.warn(
            'Company name is not provided when generating Risk Management Report'
          );
        }
        break;
      }
      case 'riskManagementSummaryReport': {
        const pdf = await generateRiskManagementSummaryReport();
        setRiskManagementSummaryReportPDF(pdf);
        break;
      }
      case 'summaryReport': {
        if (data && Array.isArray(data)) {
          const pdf = await generateFormSummaryReport(data);
          setSummaryReportPDF(pdf);
        } else {
          console.warn(
            'data is not provided or data is not FormModel[] when generating Summary Report'
          );
        }
        break;
      }
    }

    setIsGeneratingReport(false);
  };

  return { generateReportHook };
};

export default useReportGenerator;
